/**
 * Promo treatment labels (Asana 1217914880858819, Track C, Design Pass D-6).
 *
 * Scryfall's promo_types is an OPEN cosmetic vocabulary on printings that are
 * already distinct rows at correct foil prices. It is descriptive only: no
 * pricing path may import this module (the Track C PR greps for that), and
 * treatments never appear in a picker or a filter. Finish is a separate,
 * closed axis (utils/finishes.ts); the two are never conflated.
 *
 * Values arrive verbatim (e.g. 'surgefoil', 'galaxyfoil', 'textured').
 * A known treatment gets its own swatch; a new one Wizards invents falls back
 * to the graphite non-foil swatch with a derived label, so the UI never needs
 * a deploy to survive a new promo type. A treatment is never a chip, a pill
 * or tinted text (QA addendum 2): it renders as the swatch inside the finish
 * tag plus a muted "· Name" suffix after the finish word. Values that are distribution or event markers, not
 * treatments (prerelease, promopack, buyabox ...), are excluded by
 * NON_TREATMENT_PROMO_TYPES so a plain promo is never labelled as a foil
 * treatment. Both lists were reconciled on 2026-09-09 against the dev backfill's
 * 111 distinct values (16 foil-ish treatments, 302 cards carry 'textured').
 */

import { AssetUrls } from 'config/assets';

export interface PromoTreatment {
  /** Scryfall value, verbatim */
  key: string;
  label: string;
  /** The label without a trailing "Foil": the muted suffix after the finish word ("Foil · Surge") */
  short: string;
  /** Swatch URL: the treatment's own when it has one, else the graphite non-foil swatch */
  swatch: string;
}

type TreatmentSwatch = keyof typeof AssetUrls.icons.treatment;

const TREATMENTS: Record<string, { label: string; swatch?: TreatmentSwatch }> = {
  // Reconciled 2026-09-09 against the dev backfill's 111 distinct promo_types values
  // (backend .verify_finishes/b-promo-types-distinct.txt). Scryfall's value is
  // 'textured' (302 cards), not 'texturedfoil'; the alias is kept for safety.
  surgefoil: { label: 'Surge Foil', swatch: 'surge' },
  ripplefoil: { label: 'Ripple Foil', swatch: 'ripple' },
  firstplacefoil: { label: 'First Place Foil' },
  galaxyfoil: { label: 'Galaxy Foil', swatch: 'galaxy' },
  silverfoil: { label: 'Silver Foil', swatch: 'silver' },
  textured: { label: 'Textured Foil', swatch: 'textured' },
  texturedfoil: { label: 'Textured Foil', swatch: 'textured' },
  halofoil: { label: 'Halo Foil', swatch: 'halo' },
  rainbowfoil: { label: 'Rainbow Foil', swatch: 'rainbowfoil' },
  doublerainbow: { label: 'Double Rainbow' },
  raisedfoil: { label: 'Raised Foil' },
  fracturefoil: { label: 'Fracture Foil' },
  manafoil: { label: 'Mana Foil' },
  confettifoil: { label: 'Confetti Foil' },
  chocobotrackfoil: { label: 'Chocobo Track Foil' },
  dragonscalefoil: { label: 'Dragon Scale Foil' },
  singularityfoil: { label: 'Singularity Foil' },
  cosmicfoil: { label: 'Cosmic Foil' },
  gilded: { label: 'Gilded' },
  neonink: { label: 'Neon Ink' },
  oilslick: { label: 'Oil Slick' },
  stepandcompleat: { label: 'Step-and-Compleat' },
  embossed: { label: 'Embossed' },
  invisibleink: { label: 'Invisible Ink' },
  magnified: { label: 'Magnified' },
  glossy: { label: 'Glossy' },
  shatteredglass: { label: 'Shattered Glass', swatch: 'shattered' },
};

/**
 * Distribution, event, set-program and frame markers: promo_types values that are NOT
 * treatments. Every non-treatment value in the dev backfill's distinct list is here,
 * so none of them ever renders a chip. A value absent from BOTH lists is a treatment
 * Wizards invented after this list and renders the neutral grey chip (D-6).
 */
const NON_TREATMENT_PROMO_TYPES = new Set([
  'boosterfun', 'universesbeyond', 'prerelease', 'datestamped', 'stamped', 'promopack',
  'setpromo', 'mediainsert', 'planeswalkerdeck', 'starterdeck', 'boxtopper', 'playtest',
  'startercollection', 'beginnerbox', 'sourcematerial', 'bundle', 'sldbonus', 'poster',
  'buyabox', 'scroll', 'dossier', 'themepack', 'instore', 'tourney', 'serialized',
  'portrait', 'fnm', 'thick', 'judgegift', 'godzillaseries', 'japanshowcase', 'vault',
  'draculaseries', 'ravnicacity', 'release', 'imagine', 'doubleexposure', 'playerrewards',
  'wizardsplaynetwork', 'gameday', 'arenaleague', 'jpwalker', 'schinesealtart', 'convention',
  'resale', 'storechampionship', 'premiereshop', 'event', 'intropack', 'playpromo', 'concept',
  'standardshowdown', 'plastic', 'brawldeck', 'league', 'giftbox', 'commanderparty', 'duels',
  'headliner', 'openhouse', 'draftweekend', 'setextension', 'alchemy', 'metal', 'bringafriend',
  'moonlitland', 'upsidedown', 'silverscroll', 'commanderpromo', 'upsidedownback',
  // Final Fantasy set-program markers
  'ffi', 'ffii', 'ffiii', 'ffiv', 'ffv', 'ffvi', 'ffvii', 'ffviii', 'ffix', 'ffx', 'ffxi',
  'ffxii', 'ffxiii', 'ffxiv', 'ffxv', 'ffxvi',
  // earlier guesses kept so they stay excluded if they ever appear
  'launch', 'gift', 'judgeprize', 'dracula', 'promo', 'ffvbrawl', 'brawlgame',
]);

/** 'texturedfoil' -> 'Textured Foil'; 'somethingnew' -> 'Somethingnew' */
const deriveLabel = (key: string): string => {
  const base = key.endsWith('foil') && key.length > 4 ? `${key.slice(0, -4)} foil` : key;
  return base
    .split(/[\s_-]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

/** Treatments to render for a printing, in payload order; empty when none. */
export function getPromoTreatments(promoTypes: unknown): PromoTreatment[] {
  if (!Array.isArray(promoTypes)) return [];
  const seen = new Set<string>();
  const result: PromoTreatment[] = [];
  for (const raw of promoTypes) {
    if (typeof raw !== 'string') continue;
    const key = raw.trim().toLowerCase();
    if (!key || seen.has(key) || NON_TREATMENT_PROMO_TYPES.has(key)) continue;
    seen.add(key);
    const known = TREATMENTS[key];
    const label = known ? known.label : deriveLabel(key);
    result.push({
      key,
      label,
      short: label.replace(/ Foil$/, ''),
      swatch: known?.swatch ? AssetUrls.icons.treatment[known.swatch] : AssetUrls.icons.finish.nonfoil,
    });
  }
  return result;
}
